import React, { useContext, useState, useMemo, useEffect } from 'react';
import {
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormFeedback,
  Input,
  Label,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
} from 'reactstrap';
import BreadCrumb from '../Components/Common/BreadCrumb';
import TableContainer from '../Components/Common/TableContainer';
import DeleteModal from '../Components/Common/DeleteModal';
import { FaqContext } from '../context/FaqContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const Faqs = () => {
  document.title = 'سوالات متداول | فات 90';

  const { faqs, loading, error, fetchFaqs, addFaq, editFaq, removeFaq } = useContext(FaqContext);
  const [faq, setFaq] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  // گزینه‌های نوع سوال
  const questionTypeOptions = [
    { value: 'خرید و فروش', label: 'خرید و فروش' },
    { value: 'درباره ما', label: 'درباره ما' },
  ];

  // اعتبارسنجی فرم
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      question: faq?.question || '',
      answer: faq?.answer || '',
      questionType: faq?.questionType || '',
    },
    validationSchema: Yup.object({
      question: Yup.string()
        .required('لطفاً سوال را وارد کنید')
        .max(200, 'سوال باید حداکثر 200 کاراکتر باشد'),
      answer: Yup.string()
        .required('لطفاً پاسخ را وارد کنید')
        .max(1000, 'پاسخ باید حداکثر 1000 کاراکتر باشد'),
      questionType: Yup.string()
        .required('لطفاً نوع سوال را انتخاب کنید')
        .oneOf(['خرید و فروش', 'درباره ما'], 'نوع سوال باید "خرید و فروش" یا "درباره ما" باشد'),
    }),
    onSubmit: async (values) => {
      try {
        const faqDto = {
          question: values.question,
          answer: values.answer,
          questionType: values.questionType,
        };
        if (isEdit) {
          await editFaq(faq.id, faqDto);
        } else {
          await addFaq(faqDto);
        }
        toggleModal();
        validation.resetForm();
      } catch (err) {
        // خطاها توسط FaqContext مدیریت می‌شن
      }
    },
  });

  // مدیریت مودال
  const toggleModal = () => {
    if (modal) {
      setModal(false);
      setFaq(null);
      setIsEdit(false);
      validation.resetForm();
    } else {
      setModal(true);
    }
  };

  // افزودن سوال جدید
  const handleAddClick = () => {
    setIsEdit(false);
    setFaq(null);
    toggleModal();
  };

  // ویرایش سوال
  const handleEditClick = (faqData) => {
    setIsEdit(true);
    setFaq(faqData);
    toggleModal();
  };

  // حذف سوال
  const onClickDelete = (faqData) => {
    setFaq(faqData);
    setDeleteModal(true);
  };

  const handleDeleteFaq = async () => {
    if (faq) {
      try {
        await removeFaq(faq.id);
        setDeleteModal(false);
      } catch (err) {
        // خطاها توسط FaqContext مدیریت می‌شن
      }
    }
  };

  // دریافت سوالات در بارگذاری اولیه
  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  // ستون‌های جدول
  const columns = useMemo(
    () => [
      {
        header: 'شناسه',
        accessorKey: 'id',
        enableColumnFilter: false,
      },
      {
        header: 'سوال',
        accessorKey: 'question',
        enableColumnFilter: false,
      },
      {
        header: 'پاسخ',
        accessorKey: 'answer',
        enableColumnFilter: false,
        cell: (cell) => (
          <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {cell.getValue()}
          </div>
        ),
      },
      {
        header: 'نوع سوال',
        accessorKey: 'questionType',
        enableColumnFilter: false,
      },
      {
        header: 'عملیات',
        cell: (cell) => (
          <UncontrolledDropdown>
            <DropdownToggle href="#" className="btn btn-soft-secondary btn-sm" tag="button">
              <i className="ri-more-fill" />
            </DropdownToggle>
            <DropdownMenu className="dropdown-menu-end">
              <DropdownItem
                href="#"
                onClick={() => {
                  const faqData = cell.row.original;
                  handleEditClick(faqData);
                }}
              >
                <i className="ri-pencil-fill align-bottom me-2 text-muted"></i> ویرایش
              </DropdownItem>
              <DropdownItem
                href="#"
                onClick={() => {
                  const faqData = cell.row.original;
                  onClickDelete(faqData);
                }}
              >
                <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i> حذف
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
    ],
    []
  );

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteFaq}
        onCloseClick={() => setDeleteModal(false)}
      />
      <Modal isOpen={modal} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>{isEdit ? 'ویرایش سوال' : 'افزودن سوال'}</ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <ModalBody>
            <div className="mb-3">
              <Label htmlFor="question-input" className="form-label">
                سوال
              </Label>
              <Input
                type="text"
                id="question-input"
                name="question"
                value={validation.values.question}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={validation.touched.question && !!validation.errors.question}
              />
              <FormFeedback>{validation.errors.question}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label htmlFor="answer-input" className="form-label">
                پاسخ
              </Label>
              <Input
                type="textarea"
                id="answer-input"
                name="answer"
                rows="5"
                value={validation.values.answer}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={validation.touched.answer && !!validation.errors.answer}
              />
              <FormFeedback>{validation.errors.answer}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label htmlFor="questionType-input" className="form-label">
                نوع سوال
              </Label>
              <Input
                type="select"
                id="questionType-input"
                name="questionType"
                value={validation.values.questionType}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={validation.touched.questionType && !!validation.errors.questionType}
              >
                <option value="">انتخاب کنید</option>
                {questionTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
              <FormFeedback>{validation.errors.questionType}</FormFeedback>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button type="submit" color="primary" disabled={loading}>
              {isEdit ? 'به‌روزرسانی' : 'ثبت'}
            </Button>
            <Button type="button" color="secondary" onClick={toggleModal}>
              لغو
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
      <Container fluid>
        <BreadCrumb title="سوالات متداول" pageTitle="مدیریت" />
        <Row>
          <Col xl={12}>
            <Card>
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <Col>
                    <h5 className="card-title mb-0">لیست سوالات متداول</h5>
                  </Col>
                  <Col className="text-end">
                    <Button color="primary" onClick={handleAddClick}>
                      <i className="ri-add-line align-bottom me-1"></i> افزودن سوال
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody className="pt-0">
                {loading ? (
                  <div className="py-4 text-center">
                    <lord-icon
                      src="https://cdn.lordicon.com/msoeawqm.json"
                      trigger="loop"
                      colors="primary:#25a0e2,secondary:#00bd9d"
                      style={{ width: '72px', height: '72px' }}
                    ></lord-icon>
                    <h5 className="mt-4">در حال بارگذاری...</h5>
                  </div>
                ) : error ? (
                  <div className="py-4 text-center">
                    <lord-icon
                      src="https://cdn.lordicon.com/msoeawqm.json"
                      trigger="loop"
                      colors="primary:#25a0e2,secondary:#00bd9d"
                      style={{ width: '72px', height: '72px' }}
                    ></lord-icon>
                    <h5 className="mt-4">{error}</h5>
                  </div>
                ) : faqs && faqs.length > 0 ? (
                  <TableContainer
                    columns={columns}
                    data={faqs}
                    customPageSize={10}
                    divClass="table-responsive mb-1"
                    tableClass="mb-0 align-middle table-borderless"
                    theadClass="table-light text-muted"
                    SearchPlaceholder="جستجو بر اساس سوال یا نوع..."
                    showPagination={true} // یا false برای مخفی کردن جستجو و صفحه‌بندی
                  />
                ) : (
                  <div className="py-4 text-center">
                    <lord-icon
                      src="https://cdn.lordicon.com/msoeawqm.json"
                      trigger="loop"
                      colors="primary:#25a0e2,secondary:#00bd9d"
                      style={{ width: '72px', height: '72px' }}
                    ></lord-icon>
                    <h5 className="mt-4">هیچ سوالی یافت نشد</h5>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Faqs;